// components/sections/ProgressionStepsSection.js - VERSION CORRIGÉE avec données centralisées
import {
    Check,
    Medal,
    Plus,
    Trash2,
    Trophy,
    X,
    RefreshCw
} from 'lucide-react';
import React from 'react';
import { FaMoneyBillWave } from "react-icons/fa";
import { IoSchool, IoStatsChart } from "react-icons/io5";
import { PiUsersThreeFill } from "react-icons/pi";
import StatsModal from './StatsModal';

const ProgressionStepsSection = ({ 
    objectives, 
    // NOUVEAU : Recevoir les données et hooks centralisés
    data,
    hooks,
    onGlobalRefresh 
}) => {
    // NOUVEAU : Utiliser les données centralisées au lieu du hook local
    const {
        progressions = {},
        isLoading = false,
        hasError = false,
        errors = {},
        isFromCache = false
    } = data || {};

    const {
        progressionCachedHook,
        refreshAll
    } = hooks || {};

    const {
        updateMilestones,
        updateTrophyIndex,
        formatValue,
        getProgressText,
        getTopPercentileTag,
        getMilestonesForComponent,
        getCurrentTrophyIndices,
        clearError,
        refreshProgressions,
        loading: hookLoading = false,
        error: hookError = null
    } = progressionCachedHook || {};

    // Utiliser les états centralisés avec fallback sur les hooks
    const loading = isLoading || hookLoading;
    const error = errors.progressions || hookError;

    // États locaux pour l'édition (inchangés)
    const [editingMilestone, setEditingMilestone] = React.useState(null);
    const [editValue, setEditValue] = React.useState('');
    const [hoveredBar, setHoveredBar] = React.useState(null);
    const [showStatsModal, setShowStatsModal] = React.useState(false);
    const [selectedCategory, setSelectedCategory] = React.useState(null);

    // Récupérer les jalons et indices actuels depuis les données centralisées
    const milestones = getMilestonesForComponent ? getMilestonesForComponent() : {};
    const currentTrophyIndices = getCurrentTrophyIndices ? getCurrentTrophyIndices() : {};

    // Fonction de calcul du percentile (inchangée)
    const getPercentile = (category, value) => {
        switch (category) {
            case 'patrimoine':
                if (value >= 50000000) return 99.9;
                if (value >= 10000000) return 99.5;
                if (value >= 5000000) return 99;
                if (value >= 2239200) return 98;
                if (value >= 1000000) return 95;
                if (value >= 716300) return 90;
                if (value >= 500000) return 80;
                if (value >= 400000) return 70;
                if (value >= 300000) return 60;
                if (value >= 177200) return 50;
                if (value >= 100000) return 30;
                if (value >= 50000) return 20;
                if (value >= 25000) return 15;
                if (value >= 10000) return 10;
                if (value >= 1000) return 5;
                return 1;

            case 'cashflow':
                if (value >= 50000) return 99.9;
                if (value >= 20000) return 99.5;
                if (value >= 10000) return 99;
                if (value >= 5000) return 98;
                if (value >= 2000) return 95;
                if (value >= 1600) return 90;
                if (value >= 1200) return 80;
                if (value >= 800) return 65;
                if (value >= 400) return 40;
                if (value >= 200) return 25;
                if (value >= 100) return 15;
                if (value >= 50) return 10;
                if (value >= 10) return 5;
                return 1;

            case 'reseau':
                if (value >= 10000) return 99.9;
                if (value >= 5000) return 99.5;
                if (value >= 2000) return 99;
                if (value >= 1000) return 98;
                if (value >= 500) return 95;
                if (value >= 300) return 90;
                if (value >= 194) return 75;
                if (value >= 150) return 60;
                if (value >= 100) return 40;
                if (value >= 50) return 25;
                if (value >= 25) return 15;
                if (value >= 10) return 10;
                if (value >= 5) return 5;
                return 1;

            case 'maitrise':
                if (value >= 100) return 99.9;
                if (value >= 50) return 99.5;
                if (value >= 20) return 99;
                if (value >= 10) return 98;
                if (value >= 5) return 95;
                if (value >= 4) return 90;
                if (value >= 3) return 80;
                if (value >= 2) return 60;
                if (value >= 1) return 30;
                if (value >= 0.5) return 15;
                if (value >= 0.1) return 5;
                return 1;

            default:
                return 50;
        }
    };

    // Fonction pour obtenir le vrai top percentile (inchangée)
    const getRealTopPercentile = (category) => {
        const currentValue = milestones[category] && currentTrophyIndices[category] !== undefined
            ? milestones[category][currentTrophyIndices[category]] || 0
            : 0;
        const percentile = getPercentile(category, currentValue);
        const topPercent = parseFloat((100 - percentile).toFixed(4));

        let medalColor = 'text-gray-400';
        let bgColor = 'bg-gray-50';

        if (topPercent <= 1) {
            medalColor = 'text-yellow-500';
            bgColor = 'bg-yellow-50';
        } else if (topPercent <= 5) {
            medalColor = 'text-gray-400';
            bgColor = 'bg-gray-50';
        } else if (topPercent <= 10) {
            medalColor = 'text-amber-600';
            bgColor = 'bg-amber-50';
        }

        return { topPercent, medalColor, bgColor };
    };

    // Fonction de formatage améliorée (inchangée)
    const formatValueImproved = (category, value) => {
        const numberFormatOptions = {
            style: 'decimal',
            maximumFractionDigits: 0
        };

        if (category === 'patrimoine') {
            if (value >= 1000000) {
                const millions = value / 1000000;
                if (millions % 1 === 0) {
                    return `${millions}M€`;
                } else {
                    return `${millions.toFixed(1)}M€`;
                }
            } else if (value >= 1000) {
                return `${new Intl.NumberFormat('fr-FR', numberFormatOptions).format(value / 1000)}K€`;
            }
            return `${new Intl.NumberFormat('fr-FR', numberFormatOptions).format(value)}€`;
        }

        if (category === 'cashflow') {
            return `${new Intl.NumberFormat('fr-FR', numberFormatOptions).format(value)}€`;
        }

        return new Intl.NumberFormat('fr-FR', numberFormatOptions).format(value);
    };

    // NOUVEAU : Handler pour rafraîchissement avec données centralisées
    const handleProgressionRefresh = async () => {
        try {
            // Utiliser le rafraîchissement global centralisé si disponible
            if (refreshAll && typeof refreshAll === 'function') {
                refreshAll();
            } else if (refreshProgressions && typeof refreshProgressions === 'function') {
                // Fallback sur le rafraîchissement local
                await refreshProgressions();
            }
            
            // Déclencher le rafraîchissement global si disponible
            if (onGlobalRefresh) {
                onGlobalRefresh();
            }
        } catch (error) {
            // Géré silencieusement
        }
    };

    // Fonctions de gestion des jalons avec données centralisées (adaptées)
    const addMilestone = async (category) => {
        if (!updateMilestones) {
            return;
        }

        try {
            const currentMilestones = milestones[category] || [];
            if (currentMilestones.length >= 15) {
                alert('Vous ne pouvez pas avoir plus de 15 jalons par catégorie. Vous pouvez en supprimer certains ou modifier leur valeur');
                return;
            }

            const lastValue = currentMilestones[currentMilestones.length - 1] || 0;
            const increment = category === 'patrimoine' ? 100000 :
                category === 'cashflow' ? 400 :
                category === 'maitrise' ? 1 : 100;
            const newValue = lastValue + increment;
            const newMilestones = [...currentMilestones, newValue];

            const result = await updateMilestones(category, newMilestones);
            
            if (result.success) {
                const newIndex = currentMilestones.length;
                setTimeout(() => {
                    setEditingMilestone({ category, index: newIndex });
                    setEditValue(newValue.toString());
                }, 50);
            }
        } catch (error) {
            alert('Erreur lors de l\'ajout du jalon: ' + error.message);
        }
    };

    const removeMilestone = async (category, index) => {
        if (!updateMilestones || !updateTrophyIndex) {
            return;
        }

        try {
            const currentMilestones = milestones[category] || [];
            if (currentMilestones.length <= 3) return;

            if (editingMilestone?.category === category && editingMilestone?.index === index) {
                setEditingMilestone(null);
                setEditValue('');
            }

            const newMilestones = currentMilestones.filter((_, i) => i !== index);
            const result = await updateMilestones(category, newMilestones);

            if (result.success) {
                const currentTrophyIndex = currentTrophyIndices[category] || 0;
                if (currentTrophyIndex >= index) {
                    const newTrophyIndex = Math.max(0, currentTrophyIndex - 1);
                    await updateTrophyIndex(category, newTrophyIndex);
                }
            }
        } catch (error) {
            alert('Erreur lors de la suppression du jalon: ' + error.message);
        }
    };

    const startEditMilestone = (category, index, currentValue) => {
        setEditingMilestone({ category, index });
        setEditValue(currentValue.toString());
    };

    const cancelEdit = async () => {
        if (editingMilestone && updateMilestones) {
            const { category, index } = editingMilestone;
            const currentMilestones = milestones[category] || [];
            const isNewMilestone = index === currentMilestones.length - 1;

            if (isNewMilestone) {
                const lastValue = currentMilestones[currentMilestones.length - 1];
                const previousValue = currentMilestones[currentMilestones.length - 2] || 0;
                const expectedIncrement = category === 'patrimoine' ? 100000 :
                    category === 'cashflow' ? 400 :
                    category === 'maitrise' ? 1 : 100;

                if (lastValue === previousValue + expectedIncrement) {
                    try {
                        const newMilestones = currentMilestones.slice(0, -1);
                        await updateMilestones(category, newMilestones);
                    } catch (error) {
                        // Géré silencieusement
                    }
                }
            }
        }
        setEditingMilestone(null);
        setEditValue('');
    };

    const saveEditMilestone = async () => {
        if (!editingMilestone || !updateMilestones) return;

        const { category, index } = editingMilestone;
        const newValue = parseFloat(editValue.replace(/[,\s]/g, ''));

        if (isNaN(newValue) || newValue <= 0) {
            const currentMilestones = milestones[category] || [];
            const isNewMilestone = index === currentMilestones.length - 1;

            if (isNewMilestone) {
                try {
                    const newMilestones = currentMilestones.slice(0, -1);
                    await updateMilestones(category, newMilestones);
                } catch (error) {
                    // Géré silencieusement
                }
            }
            cancelEdit();
            return;
        }

        try {
            const currentMilestones = [...(milestones[category] || [])];
            const prevValue = index > 0 ? currentMilestones[index - 1] : 0;
            const nextValue = index < currentMilestones.length - 1 ? currentMilestones[index + 1] : Infinity;

            if (newValue <= prevValue || newValue >= nextValue) {
                alert(`La valeur doit être comprise entre ${formatValueImproved(category, prevValue)} et ${nextValue === Infinity ? '∞' : formatValueImproved(category, nextValue)}`);
                return;
            }

            currentMilestones[index] = newValue;
            const result = await updateMilestones(category, currentMilestones);

            if (result.success) {
                setEditingMilestone(null);
                setEditValue('');
            }
        } catch (error) {
            alert('Erreur lors de la sauvegarde: ' + error.message);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            saveEditMilestone();
        } else if (e.key === 'Escape') {
            cancelEdit();
        }
    };

    const getGradientColor = (category, index) => {
        const trophyIndex = currentTrophyIndices[category] || 0;
        if (index > trophyIndex) {
            return 'bg-gray-200';
        }

        if (index === trophyIndex) {
            return 'bg-[#18574F]';
        } else if (index < trophyIndex) {
            const greenShades = [
                'bg-[#f0f5f4]',
                'bg-[#e8f1f0]',
                'bg-[#d9efeb]',
                'bg-[#c8d7d6]',
                'bg-[#a8c4c2]',
                'bg-[#7fa8a5]'
            ];
            const shadeIndex = Math.min(
                Math.floor((index / Math.max(trophyIndex - 1, 1)) * (greenShades.length - 1)),
                greenShades.length - 1
            );
            return greenShades[shadeIndex];
        } else {
            return 'bg-gray-200';
        }
    };

    // Gestionnaires d'événements avec données centralisées
    const handleBarClick = async (category, index) => {
        if (!updateTrophyIndex) {
            return;
        }

        try {
            await updateTrophyIndex(category, index);
        } catch (error) {
            alert('Erreur lors de la mise à jour du trophée: ' + error.message);
        }
    };

    const handleMouseEnterBar = (category, index) => {
        setHoveredBar({ category, index });
    };

    const handleMouseLeaveBar = () => {
        setHoveredBar(null);
    };

    const openStatsModal = (category) => {
        setSelectedCategory(category);
        setShowStatsModal(true);
    };

    const closeStatsModal = () => {
        setShowStatsModal(false);
        setSelectedCategory(null);
    };

    // Affichage des erreurs avec possibilité de rafraîchir
    React.useEffect(() => {
        if (error) {
            // Optionnel: afficher une notification d'erreur à l'utilisateur
        }
    }, [error]);

    // Fonction pour obtenir le texte de progression avec fallback et formatage amélioré
    const getProgressTextSafe = (category) => {
        if (!progressions[category]) return '';
        try {
            const prog = progressions[category];
            const currentIndex = prog.current_trophy_index || 0;
            const categoryMilestones = milestones[category] || [];
            const currentValue = categoryMilestones[currentIndex] || 0;
            const maxValue = categoryMilestones[categoryMilestones.length - 1] || 0;

            if (category === 'reseau') {
                return `${formatValueImproved(category, currentValue)} / ${formatValueImproved(category, maxValue)} contacts`;
            }

            if (category === 'maitrise') {
                return `${formatValueImproved(category, currentValue)} / ${formatValueImproved(category, maxValue)} opérations`;
            }

            return `${formatValueImproved(category, currentValue)} / ${formatValueImproved(category, maxValue)}`;
        } catch (error) {
            const prog = progressions[category];
            const currentIndex = prog.current_trophy_index || 0;
            const categoryMilestones = milestones[category] || [];
            const currentValue = categoryMilestones[currentIndex] || 0;
            const maxValue = categoryMilestones[categoryMilestones.length - 1] || 0;

            if (category === 'reseau') {
                return `${formatValueImproved(category, currentValue)} / ${formatValueImproved(category, maxValue)} contacts`;
            }

            if (category === 'maitrise') {
                return `${formatValueImproved(category, currentValue)} / ${formatValueImproved(category, maxValue)} opérations`;
            }

            return `${formatValueImproved(category, currentValue)} / ${formatValueImproved(category, maxValue)}`;
        }
    };

    // Rendu d'une carte de progression (inchangé sauf les refs aux fonctions centralisées)
    const renderCard = (category, icon, title) => {
        const categoryMilestones = milestones[category] || [];
        const realTopPercentile = getRealTopPercentile(category);

        if (!progressions[category] && loading) {
            return (
                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                    <div className="p-4 text-center">
                        <div className="animate-pulse">
                            <div className="h-4 bg-gray-200 rounded mb-2"></div>
                            <div className="h-3 bg-gray-200 rounded"></div>
                        </div>
                    </div>
                </div>
            );
        }

        return (
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:border-gray-300 transition-colors">
                {/* En-tête */}
                <div className="bg-gray-50 px-2 py-1 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 flex items-center justify-center">
                                {React.cloneElement(icon, { className: "w-5 h-5 text-gray-600" })}
                            </div>
                            <div className="w-px h-9 bg-gray-300"></div>
                            <div>
                                <h4 className="text-sm font-medium text-gray-700">{title}</h4>
                                <p className="text-xs text-gray-500">{getProgressTextSafe(category)}</p>
                            </div>
                        </div>
                        <div className="flex items-center space-x-2">
                            {loading && (
                                <RefreshCw className="w-3 h-3 animate-spin text-gray-500" />
                            )}
                            <button
                                onClick={() => openStatsModal(category)}
                                className={`flex items-center space-x-1 px-2 py-1 rounded text-xs border hover:bg-gray-100 transition-colors ${realTopPercentile.bgColor} border-gray-300`}
                                title="Voir les statistiques nationales"
                            >
                                <Medal className={`w-3 h-3 ${realTopPercentile.medalColor}`} />
                                <span className="text-gray-600 font-medium">Top {realTopPercentile.topPercent}%</span>
                            </button>
                        </div>
                    </div>
                </div>

                {/* Barre de progression */}
                <div className="mb-12 mt-8 mx-4">
                    <div className="flex items-end space-x-1 h-16 mb-7">
                        {categoryMilestones.map((value, index) => {
                            const trophyIndex = currentTrophyIndices[category] || 0;
                            const isCurrentMilestone = index === trophyIndex;
                            const totalBars = categoryMilestones.length + 1;
                            const height = ((index + 1) / totalBars) * 100;
                            const isHovered = hoveredBar?.category === category && hoveredBar?.index === index;
                            const showHoverTrophy = isHovered && !isCurrentMilestone;

                            return (
                                <div key={index}
                                    className={`flex-1 rounded-sm relative group cursor-pointer ${getGradientColor(category, index)}`}
                                    style={{ height: `${height}%` }}
                                    onClick={() => handleBarClick(category, index)}
                                    onMouseEnter={() => handleMouseEnterBar(category, index)}
                                    onMouseLeave={handleMouseLeaveBar}
                                >
                                    {isCurrentMilestone && (
                                        <div className="absolute -top-8 left-1/2 transform -translate-x-1/2">
                                            <Trophy className="w-5 h-5 text-amber-500" fill="currentColor" />
                                        </div>
                                    )}
                                    {showHoverTrophy && (
                                        <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 transition-opacity duration-150 opacity-40">
                                            <Trophy className="w-5 h-5 text-amber-400" fill="currentColor" />
                                        </div>
                                    )}
                                    <div className="absolute inset-0 flex items-center justify-between px-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleBarClick(category, index);
                                            }}
                                            className="w-4 h-4 rounded-full bg-[#18574F] flex items-center justify-center hover:bg-[#2D7A6E] transition-colors shrink-0"
                                            title="Marquer comme objectif atteint"
                                        >
                                            <Check className="w-2.5 h-2.5 text-white stroke-2" strokeWidth={3} />
                                        </button>
                                        {categoryMilestones.length > 3 && categoryMilestones.length <= 8 && (
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    removeMilestone(category, index);
                                                }}
                                                className="w-4 h-4 rounded-full bg-[#e57a86] flex items-center justify-center hover:bg-[#d8314b] transition-colors shrink-0"
                                                title="Supprimer ce jalon"
                                            >
                                                <Trash2 className="w-2.5 h-2.5 text-white stroke-1" strokeWidth={1} />
                                            </button>
                                        )}
                                        {categoryMilestones.length > 8 && isHovered && (
                                            <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 flex space-x-1 bg-white shadow-lg rounded-md p-1 border z-10">
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleBarClick(category, index);
                                                    }}
                                                    className="w-6 h-6 rounded-full bg-[#18574F] flex items-center justify-center hover:bg-[#2D7A6E] transition-colors"
                                                    title="Marquer comme objectif atteint"
                                                >
                                                    <Check className="w-3 h-3 text-white stroke-2" strokeWidth={3} />
                                                </button>
                                                {categoryMilestones.length > 3 && (
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            removeMilestone(category, index);
                                                        }}
                                                        className="w-6 h-6 rounded-full bg-[#e57a86] flex items-center justify-center hover:bg-[#d8314b] transition-colors"
                                                        title="Supprimer ce jalon"
                                                    >
                                                        <Trash2 className="w-3 h-3 text-white stroke-1" strokeWidth={1} />
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                        {/* Barre "+" pour ajouter */}
                        <div
                            className="flex-1 bg-gray-200 rounded-sm relative cursor-pointer hover:bg-gray-300 transition-colors group"
                            style={{ height: '100%' }}
                            onClick={() => addMilestone(category)}
                        >
                            <div className="absolute inset-0 flex items-center justify-center">
                                <Plus className="w-3 h-3 text-gray-500 group-hover:text-gray-700" />
                            </div>
                        </div>
                    </div>

                    {/* Valeurs des jalons */}
                    <div className="flex space-x-1 h-8 relative -mb-4">
                        {categoryMilestones.map((value, index) => (
                            <div key={index} className="flex-1 relative group">
                                {editingMilestone?.category === category && editingMilestone?.index === index ? (
                                    <input
                                        type="text"
                                        value={editValue}
                                        onChange={(e) => setEditValue(e.target.value)}
                                        onBlur={saveEditMilestone}
                                        onKeyDown={handleKeyPress}
                                        className="absolute left-1/2 top-1 -translate-x-1/2 origin-bottom-left transform -rotate-45 text-xs bg-white border border-[#18574F] rounded px-1 py-0.5 w-24 text-center focus:outline-none focus:ring-1 focus:ring-[#18574F]"
                                        autoFocus
                                        placeholder="Saisir valeur"
                                    />
                                ) : (
                                    <div className={`absolute left-1/2 top-1 -translate-x-1/2 origin-bottom-left transform -rotate-45 text-xs whitespace-nowrap cursor-pointer hover:bg-gray-50 px-1 py-0.5 rounded transition-colors ${
                                        index === (currentTrophyIndices[category] || 0) ? 'font-medium text-[#18574F]' : 'text-gray-500'
                                    }`}
                                        onClick={() => startEditMilestone(category, index, value)}
                                        title="Cliquer pour modifier">
                                        {formatValueImproved(category, value)}
                                    </div>
                                )}
                            </div>
                        ))}
                        <div className="flex-1 relative">
                            <div className="absolute left-1/2 top-6 -translate-x-1/2 origin-bottom-left -rotate-45 transform text-xs whitespace-nowrap text-gray-400 ">
                                Ajouter cible
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // Affichage principal avec gestion du loading et des erreurs
    if (loading && Object.keys(progressions).length === 0) {
        return (
            <div className="grid grid-cols-4 gap-4">
                {['patrimoine', 'cashflow', 'reseau', 'maitrise'].map(category => (
                    <div key={category} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                        <div className="p-4 text-center">
                            <div className="animate-pulse">
                                <div className="h-4 bg-gray-200 rounded mb-2"></div>
                                <div className="h-3 bg-gray-200 rounded"></div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    return (
        <div className="">
            {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-center justify-between">
                        <p className="text-sm text-red-600">{error}</p>
                        <div className="flex items-center space-x-2">
                            <button
                                onClick={handleProgressionRefresh}
                                className="text-blue-600 hover:text-blue-800 text-sm"
                                title="Actualiser les données"
                            >
                                <RefreshCw className="w-4 h-4" />
                            </button>
                            <button
                                onClick={clearError}
                                className="text-red-400 hover:text-red-600"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="grid grid-cols-4 gap-4">
                {/* Carte 1 - Patrimoine */}
                {renderCard(
                    'patrimoine',
                    <IoStatsChart />,
                    'Patrimoine net'
                )}

                {/* Carte 2 - Cashflow */}
                {renderCard(
                    'cashflow',
                    <FaMoneyBillWave />,
                    'Cashflow mensuel'
                )}

                {/* Carte 3 - Réseau */}
                {renderCard(
                    'reseau',
                    <PiUsersThreeFill />,
                    'Réseau'
                )}

                {/* Carte 4 - Maîtrise immobilière */}
                {renderCard(
                    'maitrise',
                    <IoSchool />,
                    'Maîtrise immobilière'
                )}
            </div>

            <StatsModal
                showStatsModal={showStatsModal}
                selectedCategory={selectedCategory}
                closeStatsModal={closeStatsModal}
                formatValue={formatValueImproved}
                milestones={milestones}
                currentTrophyIndices={currentTrophyIndices}
            />
        </div>
    );
};

export default ProgressionStepsSection;